import { type ReactNode } from 'react';
import { IconButton } from '@understory-io/leaf/icon-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { VisuallyHidden } from '@understory-io/leaf/visually-hidden';
import { FormattedMessage } from 'react-intl';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { css } from '@understory-io/leaf/css';
import { CloseIcon } from '~/ui/icons/close';

type ModalDialogProps = {
  /** Dialog title displayed in the header */
  title: ReactNode;
  /**
   * Optional action(s) rendered on the title row, right-aligned. Use for
   * header-level affordances like a Filters button. The close button sits
   * on its own row above and is owned by the dialog.
   */
  actions?: ReactNode;
  /**
   * Dialog content. Place a `<ModalDialog.Footer>` as a direct child
   * to pin content (e.g. total row + action button) at the bottom.
   */
  children: ReactNode;
  /** Called when the dialog should close (escape, click outside, close button) */
  onClose: () => void;
};

export type { ModalDialogProps };

/**
 * A responsive dialog: bottom sheet on mobile, centered modal on tablet+.
 *
 * Only `BookingDialog` still uses this — everything else moved to Leaf's
 * `Dialog`. It stays because Leaf has no equivalent for two things the booking
 * flow needs: an `actions` slot on the title row (the filter button), and a
 * footer that holds content other than buttons (the pinned "pick a time"
 * error). Both are deferred in Leaf; see the Dialog PR.
 *
 * Renders a fixed header with title + close button, a scrollable content area,
 * and an optional fixed footer. Dismisses via Escape, click outside, or close button.
 *
 * Use `<ModalDialog.Footer>` inside children to declare fixed bottom content.
 */
export function ModalDialog({
  title,
  actions,
  children,
  onClose,
}: ModalDialogProps) {
  return (
    <ModalOverlay
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable
      className={overlayStyle}
    >
      <Modal className={modalStyle}>
        <Dialog className={dialogStyle}>
          {/* Header — fixed */}
          <div className={headerStyle}>
            <Stack direction="row" justify="flex-end">
              <IconButton appearance="text" size="standard" onClick={onClose}>
                <CloseIcon />
                <VisuallyHidden>
                  <FormattedMessage
                    defaultMessage="Close"
                    description="Close dialog"
                  />
                </VisuallyHidden>
              </IconButton>
            </Stack>
            <Stack
              direction="row"
              justify="space-between"
              align="center"
              gap="small"
            >
              <Heading slot="title">
                <Text style="subheadline-large">{title}</Text>
              </Heading>
              {actions && <div className={actionsStyle}>{actions}</div>}
            </Stack>
          </div>

          {/* Body — scrollable */}
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

export function Body({ children }: { children: ReactNode }) {
  return <div className={bodyStyle}>{children}</div>;
}

/**
 * Fixed footer area inside a `ModalDialog`. Place as a direct child of
 * `<ModalDialog>` — it will be hoisted out of the scroll area and pinned
 * to the bottom.
 */
export function Footer({ children }: { children: ReactNode }) {
  return <div className={footerStyle}>{children}</div>;
}

const overlayStyle = css({
  position: 'fixed',
  inset: 'none',
  zIndex: '[50]',
  display: 'flex',
  alignItems: { base: 'flex-end', tablet: 'center' },
  justifyContent: 'center',
  background: 'surface.overlay',
  // The `surface.overlay` scrim alone is very faint (10% in light mode), so the
  // page behind stays busy and competes with the dialog. Blurring it pushes the
  // background out of focus, matching how backoffice separates its dialogs.
  // Leaf has no blur token, hence the escape hatch; Panda emits the
  // `-webkit-` prefix for us.
  backdropFilter: '[blur(5px)]',
});

const modalStyle = css({
  // Mobile: full-bleed bottom sheet. Tablet+: 600px wide with a `large` (24px)
  // safe margin from the viewport sides and top/bottom so the dialog never
  // touches the screen edges.
  width: { base: 'full', tablet: '[calc(100vw - 48px)]' },
  maxWidth: { tablet: '[600px]' },
  maxHeight: { base: '[100dvh]', tablet: '[calc(100dvh - 48px)]' },
  background: 'surface.base',
  borderTopLeftRadius: 'xlarge',
  borderTopRightRadius: 'xlarge',
  borderBottomLeftRadius: { base: 'none', tablet: 'xlarge' },
  borderBottomRightRadius: { base: 'none', tablet: 'xlarge' },
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const dialogStyle = css({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
  flex: '1',
  minHeight: 'none',
  // Horizontal padding lives on header/body/footer individually so that
  // selection outlines on cards inside the scrollable body can extend INTO
  // the body's own padding without being clipped by its overflow boundary.
  paddingY: { base: 'large', tablet: 'large', desktop: 'xlarge' },
  color: 'content.primary',
});

const sectionPaddingX = {
  base: 'medium',
  tablet: 'large',
  desktop: 'xlarge',
} as const;

const headerStyle = css({
  flexShrink: '0',
  paddingX: sectionPaddingX,
  // Breathing room between the title and the scrollable body — without it the
  // title sits visually flush with the first body row.
  paddingBottom: 'medium',
});

const bodyStyle = css({
  flex: '1',
  overflowY: 'auto',
  paddingX: sectionPaddingX,
  paddingY: 'medium',
  display: 'flex',
  flexDirection: 'column',
  gap: 'large',
});

const actionsStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'xsmall',
  flexShrink: '0',
});

const footerStyle = css({
  flexShrink: '0',
  display: 'flex',
  flexDirection: 'column',
  gap: 'large',
  paddingX: sectionPaddingX,
  // Mirror the header's bottom spacing so the button isn't flush against the
  // scrollable body above it.
  paddingTop: 'medium',
});
